import { Clock3 } from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ThreeDots } from "react-loader-spinner";
import Webcam from "react-webcam";
import { API_BASE_URL } from "../../config";

// Define types
type Question = {
  question: string | string[];
  options: string[];
  marks: number;
  questionType: "multiple-choice" | "fillInBlank";
  correctAnswer: string | string[];
  selectedAnswer: string | string[];
};

type QuestionsData = {
  [key: string]: Question[];
};

const getFillInBlankMarks = (
  selectedAnswer: string,
  correctAnswer: string | string[],
  isTwoAnswerQuestion: boolean
): number => {
  const selected = (selectedAnswer || "").trim().toLowerCase();
  if (!selected) return 0;

  const correctAnswersList = Array.isArray(correctAnswer)
    ? correctAnswer
    : [correctAnswer];

  if (isTwoAnswerQuestion) {
    const blank1Answers = new Set<string>();
    const blank2Answers = new Set<string>();

    correctAnswersList.forEach((ans) => {
      const splitParts = ans.split(/\s*,\s*|\s+and\s+|\s*&\s*|\s*\/\s*/i);
      if (splitParts.length > 1) {
        const p1 = splitParts[0].trim().toLowerCase();
        const p2 = splitParts[1].trim().toLowerCase();
        if (p1) blank1Answers.add(p1);
        if (p2) blank2Answers.add(p2);
      }
    });

    let matchedBlank1 = false;
    let matchedBlank2 = false;

    blank1Answers.forEach((b1) => {
      const regex = new RegExp(`\\b${b1}\\b`, "i");
      if (regex.test(selected) || selected === b1) {
        matchedBlank1 = true;
      }
    });

    blank2Answers.forEach((b2) => {
      const regex = new RegExp(`\\b${b2}\\b`, "i");
      if (regex.test(selected) || selected === b2) {
        matchedBlank2 = true;
      }
    });

    if (matchedBlank1 && matchedBlank2) {
      return 1;
    } else if (matchedBlank1 || matchedBlank2) {
      return 0.5;
    }
    return 0;
  } else {
    // Standard single answer
    const isCorrect = correctAnswersList.some(
      (correct) => correct.trim().toLowerCase() === selected
    );
    return isCorrect ? 1 : 0;
  }
};

const questionsData: QuestionsData = {
  logicalPart_A: [
    {
      question: "1) If A = 1, B = 2, C = 3, what is the value of M?",
      options: ["10", "12", "13", "14"],
      marks: 1,
      correctAnswer: "13",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: " 2) Identify the odd one out:",
      options: ["Cat", "Dog", "Fish", "Apple"],
      marks: 1,
      correctAnswer: "apple",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "3) Complete the number sequence: 3, 6, 9, 12, __, 18, 21",
      options: [],
      marks: 1,
      correctAnswer: ["15", "fifteen"],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question:
        '4) If "1 week = 7 days" and "1 day = 24 hours," how many hours are in 2 weeks?',
      options: [],
      marks: 1,
      correctAnswer: [
        "336",
        "336 hours",
        "336hours",
        "336 hrs",
        "336hrs",
        "three hundred thirty six",
        "three hundred and thirty six",
        "three hundred thirty-six",
        "three hundred and thirty-six",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "5) Rearrange the letters to form a meaningful word: ERAC",
      options: [],
      marks: 1,
      correctAnswer: ["care", "race", "acre"],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "6) If A = 1, B = 2, C = 3, what is the value of H?",
      options: ["6", "7", "8", "9"],
      marks: 1,
      correctAnswer: "8",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "7) Identify the odd one out:",
      options: ["Sun", "Moon", "Star", "Chair"],
      marks: 1,
      correctAnswer: "chair",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question:
        "8) If a car travels 60 km in 1 hour, its speed is ______ km/h.",
      options: [],
      marks: 1,
      correctAnswer: [
        "60",
        "60 km/h",
        "60km/h",
        "60 km",
        "60km",
        "60 kmh",
        "60kmh",
        "sixty",
        "sixty km/h",
        "sixty km",
        "sixtykm/h",
        "sixtykm",
        "sixty kmh",
        "sixtykmh",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "9) Complete the pattern: 2, 4, 8, __, 32",
      options: [],
      marks: 1,
      correctAnswer: ["16", "sixteen"],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "10) Which word does not belong in the group?",
      options: ["Apple", "Banana", "Carrot", "Orange"],
      marks: 1,
      correctAnswer: "carrot",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
  ],
  logicalPart_B: [
    {
      question: "1) If a square has 4 sides, a triangle has ___ sides.",
      options: [],
      marks: 1,
      correctAnswer: ["3", "3 sides", "3sides", "three", "three sides", "threesides"],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question:
        "2) If 1 minute = 60 seconds, then half a minute = ___ seconds.",
      options: [],
      marks: 1,
      correctAnswer: [
        "30",
        "30 seconds",
        "30seconds",
        "30 sec",
        "30sec",
        "thirty",
        "thirty seconds",
        "thirtyseconds",
        "thirty sec",
        "thirtysec",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "3) If today is Friday, the day after tomorrow is ___.",
      options: [],
      marks: 1,
      correctAnswer: "sunday",
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "4) The sun sets in the ___, and the moon appears in the ___.",
      options: [],
      marks: 1,
      correctAnswer: [
        "west, night",
        "west,night",
        "west , night",
        "west and night",
        "west/night",
        "west",
        "night",
        "west night",
        "west and night time",
        "west, night time",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "5) If a car has 4 wheels, a bicycle has ___ wheels.",
      options: [],
      marks: 1,
      correctAnswer: ["2", "2 wheels", "2wheels", "two", "two wheels", "twowheels"],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "6) If 1 minute = 60 seconds, then 10 minutes = ___ seconds.",
      options: [],
      marks: 1,
      correctAnswer: [
        "600",
        "600 seconds",
        "600seconds",
        "600 sec",
        "600sec",
        "six hundred",
        "six hundred seconds",
        "six hundredseconds",
        "six hundred sec",
        "six hundredsec",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "7) If today is Wednesday, the day after tomorrow is ___.",
      options: [],
      marks: 1,
      correctAnswer: "friday",
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "8) The sun rises in the ___ and sets in the ___.",
      options: [],
      marks: 1,
      correctAnswer: [
        "east, west",
        "east,west",
        "east and west",
        "east & west",
        "east , west",
        "east/west",
        "east west",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "9) Rearrange the letters to form a meaningful word: TAC",
      options: [],
      marks: 1,
      correctAnswer: ["cat", "act"],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question:
        "10) A farmer has 10 sheep. All but 3 run away. How many are left?",
      options: [],
      marks: 1,
      correctAnswer: ["3", "3 sheep", "3sheep", "three", "three sheep", "threesheep"],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question:
        "11) If 3 cats can catch 3 mice in 3 minutes, how long will it take for 6 cats to catch 6 mice?",
      options: [],
      marks: 1,
      correctAnswer: [
        "3",
        "3 minutes",
        "3minutes",
        "3 mins",
        "3mins",
        "3 min",
        "3min",
        "three",
        "three minutes",
        "threeminutes",
        "three mins",
        "threemins",
        "three min",
        "threemin",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question:
        "12) If Monday comes after Sunday, then what comes two days before Thursday?",
      options: [],
      marks: 1,
      correctAnswer: "tuesday",
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question:
        "13) A book has 400 pages. If you tear out pages 21 to 50, how many pages are left?",
      options: [],
      marks: 1,
      correctAnswer: [
        "370",
        "370 pages",
        "370pages",
        "three hundred seventy",
        "three hundred and seventy",
        "three hundred seventy pages",
        "three hundred and seventy pages",
        "three hundred seventy-pages",
        "three hundred seventy-page",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question:
        "14) A clock gains 5 minutes every hour. If it starts at 12:00 noon, what time will it show after 6 real hours?",
      options: [],
      marks: 1,
      correctAnswer: [
        "6:30",
        "6:30 pm",
        "6:30pm",
        "6.30",
        "6.30pm",
        "6.30 pm",
        "six thirty",
        "six thirty pm",
        "six thirtypm",
      ],
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "15) Fill in the missing letter: B __ N A N A",
      options: [],
      marks: 1,
      correctAnswer: "a",
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "16) Fill in the missing letter: E L __ P H A N T",
      options: [],
      marks: 1,
      correctAnswer: "e",
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
    {
      question: "17) Fill in the missing letter: M __ U N T A I N",
      options: [],
      marks: 1,
      correctAnswer: "o",
      selectedAnswer: "",
      questionType: "fillInBlank",
    },
  ],
  logicalPart_C: [
    {
      question: "1) What does Aisha love doing?",
      options: [
        "Cooking food for patients",
        "Helping patients recover",
        "Taking care of hospital equipment",
        "Giving hospital tours",
      ],
      marks: 1,
      correctAnswer: "helping patients recover",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "2) Where does she work every day?",
      options: [
        "At a restaurant",
        "At a hospital",
        "At a school",
        "At a supermarket",
      ],
      marks: 1,
      correctAnswer: "at a hospital",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "3) What did she do to help a patient one day?",
      options: [
        "Gave them extra medicine",
        "Helped them with simple exercises",
        "Advised them to leave the hospital",
        "Read them a book",
      ],
      marks: 1,
      correctAnswer: "helped them with simple exercises",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "4) Who praised her work?",
      options: [
        "A hospital visitor",
        "The patient`s family",
        "The doctor",
        "A fellow nurse",
      ],
      marks: 1,
      correctAnswer: "the doctor",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "5) How did Aisha feel after being praised?",
      options: [
        "Proud and motivated",
        "Nervous and unsure",
        "Tired and exhausted",
        "Confused about her work",
      ],
      marks: 1,
      correctAnswer: "proud and motivated",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
  ],
  logicalPart_D: [
    {
      question: "1) What problem did the nurse notice?",
      options: [
        "Patients were refusing to take medicine.",
        "Patients were forgetting to take their medicine on time.",
        "The hospital did not have enough medicine.",
        "Doctors were not prescribing medicines correctly.",
      ],
      marks: 1,
      correctAnswer: "patients were forgetting to take their medicine on time.",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "2) How did she solve the issue?",
      options: [
        "She gave patients extra medicine.",
        "She asked doctors to change the medicines.",
        "She designed a system with color-coded charts and reminder alarms.",
        "She reduced the number of medicines prescribed.",
      ],
      marks: 1,
      correctAnswer:
        "she designed a system with color-coded charts and reminder alarms.",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "3) Why was her solution important?",
      options: [
        "It helped patients follow their medication schedule and recover faster.",
        "It allowed nurses to reduce their workload.",
        "It helped doctors prescribe stronger medicines.",
        "It allowed patients to take any medicine they wanted.",
      ],
      marks: 1,
      correctAnswer:
        "it helped patients follow their medication schedule and recover faster.",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question: "4) What was the result of using her system?",
      options: [
        "Patients started taking their medicines on time and recovered faster",
        "The hospital ran out of medicines.",
        "Doctors had to change their treatment plans frequently.",
        "Patients stopped coming to the hospital.",
      ],
      marks: 1,
      correctAnswer:
        "patients started taking their medicines on time and recovered faster",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
    {
      question:
        "5) Why is it important for patients to take medicines on time?",
      options: [
        "It ensures proper treatment and helps in faster healing.",
        "It allows patients to take fewer medicines.",
        "It helps nurses avoid their daily rounds.",
        "It reduces the cost of hospital bills.",
      ],
      marks: 1,
      correctAnswer: "it ensures proper treatment and helps in faster healing.",
      selectedAnswer: "",
      questionType: "multiple-choice",
    },
  ],
};

const LogicalTest = () => {
  const navigate = useNavigate();
  const [userAnswers, setUserAnswers] = useState<{ [key: string]: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(17 * 60); // 17 minutes in seconds
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningCount, setWarningCount] = useState(() => {
    // Get existing warning count from sessionStorage or start from 0
    const existingCount = sessionStorage.getItem("warningCount");
    return existingCount ? parseInt(existingCount, 10) : 0;
  });
  const webcamRef = React.useRef<Webcam>(null);

  // Fetch user ID from LocalStorage
  const questionId = localStorage.getItem("questionId");

  // Function to update warning count on server
  const updateWarningCount = async (count: number) => {
    try {
      const warningId = sessionStorage.getItem("warningId");
      if (!warningId) {
        console.warn("Warning ID not found in session");
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/warning/update/${warningId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            warningCount: count.toString(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update warning count: ${response.status}`);
      }

      console.log(`Warning count updated to ${count}`);
    } catch (error) {
      console.error("Error updating warning count:", error);
    }
  };

  const lastWarningTimeRef = React.useRef<number>(0);

  // Function to handle warning
  const handleWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current < 3000) {
      return;
    }
    lastWarningTimeRef.current = now;

    setWarningCount((prev) => {
      const newCount = prev + 1;
      // Update warning count on server
      updateWarningCount(newCount);
      // Save warning count to sessionStorage for continuity across pages
      sessionStorage.setItem("warningCount", newCount.toString());
      return newCount;
    });
    setShowWarningModal(true);
  }, []);

  // Add a new function to handle auto-submission
  const handleAutoSubmit = useCallback(async () => {
    setIsSubmitting(true);

    // Save remaining time to localStorage
    const remainingTime = timeLeft;
    localStorage.setItem("remainingTime", remainingTime.toString());

    // Update marks based on correctness
    const updatedData = { ...questionsData };
    Object.keys(updatedData).forEach((section) => {
      updatedData[section].forEach((question, index) => {
        const selectedAnswer = userAnswers[`${section}_${index}`];

        if (selectedAnswer) {
          if (question.questionType === "fillInBlank") {
            const questionText = Array.isArray(question.question)
              ? question.question.join(" ")
              : question.question || "";
            const isTwoAnswerQuestion = (questionText.match(/__+/g) || []).length >= 2;
            question.marks = getFillInBlankMarks(selectedAnswer, question.correctAnswer, isTwoAnswerQuestion);
          } else {
            // For multiple choice questions
            const correctStr = Array.isArray(question.correctAnswer)
              ? question.correctAnswer[0]
              : question.correctAnswer;
            question.marks =
              correctStr.toLowerCase() === selectedAnswer.trim().toLowerCase()
                ? 1
                : 0;
          }
          question.selectedAnswer = selectedAnswer.trim();
        } else {
          question.marks = 0;
          question.selectedAnswer = "";
        }
      });
    });

    // Send the updated data using fetch
    try {
      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (response.ok) {
        navigate("/memorization");
      } else {
        throw new Error("Failed to update answers");
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [timeLeft, userAnswers, questionId, navigate]);

  useEffect(() => {
    // Prevent going back
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = function () {
      window.history.pushState(null, "", window.location.href);
    };

    // Handle page reload/close
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Prevent tab switching
    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleWarning();
        // Force focus back to the test window
        window.focus();
      }
    };

    const handleBlur = () => {
      handleWarning();
      // Force focus back to the test window
      window.focus();
    };

    // Add event listeners for tab switching prevention
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    if (!questionId) {
      navigate("/registration");
      return;
    }
    if (timeLeft > 0 && !submitted) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener("beforeunload", handleBeforeUnload);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
        window.removeEventListener("blur", handleBlur);
      };
    } else if (timeLeft === 0 && !submitted) {
      // Auto-submit when timer reaches zero
      setSubmitted(true);
      handleAutoSubmit();
    }
  }, [
    timeLeft,
    submitted,
    questionId,
    navigate,
    handleAutoSubmit,
    handleWarning,
  ]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));
  };

  const handleSubmit = () => {
    setShowModal(true);
  };

  const confirmSubmit = async () => {
    setIsSubmitting(true);
    setSubmitted(true);

    // Save remaining time to localStorage
    const remainingTime = timeLeft;
    localStorage.setItem("remainingTime", remainingTime.toString());

    // Update marks based on correctness
    const updatedData = { ...questionsData };
    Object.keys(updatedData).forEach((section) => {
      updatedData[section].forEach((question, index) => {
        const selectedAnswer = userAnswers[`${section}_${index}`];

        if (selectedAnswer) {
          if (question.questionType === "fillInBlank") {
            const questionText = Array.isArray(question.question)
              ? question.question.join(" ")
              : question.question || "";
            const isTwoAnswerQuestion = (questionText.match(/__+/g) || []).length >= 2;
            question.marks = getFillInBlankMarks(selectedAnswer, question.correctAnswer, isTwoAnswerQuestion);
          } else {
            // For multiple choice questions
            const correctStr = Array.isArray(question.correctAnswer)
              ? question.correctAnswer[0]
              : question.correctAnswer;
            question.marks =
              correctStr.toLowerCase() === selectedAnswer.trim().toLowerCase()
                ? 1
                : 0;
          }
          question.selectedAnswer = selectedAnswer.trim();
        } else {
          question.marks = 0;
          question.selectedAnswer = "";
        }
      });
    });

    // Send the updated data using fetch
    try {
      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${questionId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (response.ok) {
        navigate("/memorization");
      } else {
        throw new Error("Failed to update answers");
      }
    } catch (error) {
      console.error("Error submitting answers:", error);
      alert("Submission failed. Please check your internet connection and try clicking Next again.");
      setSubmitted(false);
    } finally {
      setIsSubmitting(false);
      setShowModal(false);
    }
  };

  const renderMultipleChoiceQuestion = (
    question: Question,
    section: string,
    index: number
  ) => {
    const questionId = `${section}_${index}`;
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-4" key={questionId}>
        <div className="flex mb-4">
          <span className="font-semibold text-xl ml-2">
            {question.question}
          </span>
        </div>
        <div className="space-y-2">
          {question.options.map((option, optIndex) => (
            <div
              key={optIndex}
              className={`border rounded-md p-3 hover:bg-gray-50 cursor-pointer transition-colors font-semibold text-lg ${
                userAnswers[questionId] === option
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200"
              }`}
              onClick={() => handleAnswerChange(questionId, option)}
            >
              <div className="flex items-center">
                <div
                  className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center border ${
                    userAnswers[questionId] === option
                      ? "border-blue-500"
                      : "border-gray-300"
                  }`}
                >
                  {userAnswers[questionId] === option && (
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  )}
                </div>
                <label className="w-full cursor-pointer">{option}</label>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderFillInBlankQuestion = (
    question: Question,
    section: string,
    index: number
  ) => {
    const questionId = `${section}_${index}`;
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 mb-4" key={questionId}>
        <div className="flex mb-4">
          <span className="font-semibold text-xl ml-2">
            {question.question}
          </span>
        </div>
        <div className="w-full">
          <input
            type="text"
            placeholder="Enter your answer..."
            value={userAnswers[questionId] || ""}
            onChange={(e) => handleAnswerChange(questionId, e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2 focus:border-blue-500 focus:ring-blue-500 font-semibold text-lg"
            disabled={submitted}
          />
        </div>
      </div>
    );
  };

  const renderSection = (
    sectionName: string,
    questions: Question[],
    title?: string,
    subtitle?: string,
    explanation?: string
  ) => {
    return (
      <div className="mb-8 bg-white/80 border border-red-100 p-8 rounded-3xl shadow-xl">
        <h2 className="bg-clip-text bg-gradient-to-r text-3xl text-center text-transparent font-bold from-red-600 to-pink-600 mb-3">
          {title}
        </h2>
        {subtitle && (
          <p className="text-black text-center text-lg font-medium mb-2">
            {subtitle}
          </p>
        )}
        {explanation && (
          <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-4">
            <p className="text-black text-md font-medium leading-relaxed italic">
              {explanation}
            </p>
          </div>
        )}
        <div className="bg-gradient-to-r h-px w-full from-red-100 mb-8 to-pink-100"></div>
        {questions.map((question: Question, index: number) => {
          return question.questionType === "multiple-choice"
            ? renderMultipleChoiceQuestion(question, sectionName, index)
            : renderFillInBlankQuestion(question, sectionName, index);
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-200 justify-center items-center px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Webcam */}
        <div className="fixed bottom-5 right-5 z-20">
          <div className="bg-white/90 border-2 border-red-200 p-1 rounded-xl custom-shadow">
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{
                width: 150,
                height: 100,
                facingMode: "user",
              }}
              className="rounded-lg"
            />
          </div>
        </div>

        {/* Timer */}
        <div className="bg-white/90 border-2 border-red-200 p-3 rounded-xl  custom-shadow timer-container fixed top-5 right-5 z-20">
          <div className="flex gap-2 items-center">
            <div className="bg-gradient-to-r p-2 rounded-full from-red-500 to-pink-600">
              <Clock3 className="text-white " />
            </div>
            <div>
              <p className="text-gray-500 text-xs font-medium">
                Time Remaining
              </p>
              <p id="timerDisplay" className="text-gray-800 text-lg font-bold">
                {formatTime(timeLeft)}
              </p>
            </div>
          </div>
        </div>

        {/* Header Card */}
        <div
          className="bg-white p-8 rounded-3xl shadow-lg mb-10 overflow-hidden relative
        "
        >
          <div className="bg-red-50 h-40 rounded-full w-40 -mr-12 -mt-12 absolute opacity-70 right-0 top-0"></div>
          <div className="bg-red-50 h-32 rounded-full w-32 -mb-8 -ml-8 absolute bottom-0 left-0 opacity-70"></div>
          <div className="relative z-10">
            <h1 className="bg-clip-text bg-gradient-to-r text-4xl text-transparent font-bold from-red-600 mb-5 to-pink-600">
              Logical Understanding
            </h1>
            <div className="bg-gradient-to-r h-1.5 rounded-full w-24 from-red-500 mb-6 to-pink-500"></div>
          </div>
        </div>

        {/* Questions Container */}
        <form id="assessmentForm" className="space-y-16" autoComplete="off">
          {renderSection(
            "logicalPart_A",
            questionsData.logicalPart_A,
            "Part A: Number & Pattern Recognition",
            "(Choose the correct answer)"
          )}
          {renderSection(
            "logicalPart_B",
            questionsData.logicalPart_B,
            "Part B: Logic & Reasoning",
            "(Fill in the blanks)"
          )}
          {renderSection(
            "logicalPart_C",
            questionsData.logicalPart_C,
            "Part C: Short Passage & Questions",
            "Read the passage below and answer the questions.",
            "A young nurse named Aisha enjoys helping patients recover. Every day, she carefully monitors their health and ensures they take their medicines on time. One day, she helped an elderly patient who was feeling weak by guiding them through simple exercises. The doctor noticed Aisha's dedication and appreciated her efforts. Aisha felt proud and continued to care for her patients with even more dedication."
          )}
          {renderSection(
            "logicalPart_D",
            questionsData.logicalPart_D,
            "",
            "Read the passage and answer the questions.",
            "A young nurse was working in a hospital where many patients had difficulty remembering to take their medicines on time. She noticed that this often led to complications in their recovery. After discussing with doctors, she designed a simple system using color-coded medicine charts and reminder alarms. Over time, patients started following their medication schedules properly, leading to faster recoveries and fewer hospital visits. Her idea was later implemented in different hospitals to improve patient care."
          )}
        </form>

        {/* Submit Button */}
        <div className="flex justify-center mb-8 mt-12">
          <button
            onClick={handleSubmit}
            className="bg-gradient-to-r rounded-xl shadow-lg text-white w-1/2 duration-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-red-500 font-bold from-red-500 hover:scale-[1.02] hover:shadow-2xl px-4 py-3 to-pink-600 transform transition-all"
            disabled={submitted}
          >
            <div className="flex justify-center items-center">
              <span className="mr-2">Next</span>
            </div>
          </button>
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Confirm Submission
              </h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to submit and proceed to the next section?
                You won't be able to return to this section.
              </p>
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSubmit}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg flex items-center justify-center min-w-[100px]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <ThreeDots
                      height="24"
                      width="50"
                      color="#ffffff"
                      ariaLabel="loading"
                      wrapperClass="flex justify-center items-center"
                    />
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Warning Modal */}
        {showWarningModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-red-600 mb-4">
                Warning: Tab Switching Detected
              </h2>
              <p className="text-gray-600 mb-2">
                Please do not switch tabs or windows during the test. This may
                result in test termination.
              </p>
              <p className="text-red-500 font-semibold mb-6">
                Warning Count: {warningCount}
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setShowWarningModal(false);
                    window.focus();
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-lg"
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogicalTest;
